import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ThesisBindingType, ThesisDegreeLevel } from "@/lib/api/bindery";
import { BINDING_TYPE_LABELS, DEGREE_LEVEL_LABELS } from "@/pages/bindery/binderyFormatters";
import { RequiredLabel } from "./RequiredLabel";
import { MIN_COPIES, MIN_PAGES } from "./newThesisConstants";
import type { ThesisFormState } from "./useThesisForm";

interface Props {
  form: ThesisFormState;
  setField: <K extends keyof ThesisFormState>(k: K, v: ThesisFormState[K]) => void;
  markTouched: (k: keyof ThesisFormState) => void;
  errorFor: (k: keyof ThesisFormState) => string | undefined;
}

export function ThesisDetailsCard({ form, setField, markTouched, errorFor }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <RequiredLabel required>Degree level</RequiredLabel>
            <Select
              value={form.degreeLevel}
              onValueChange={(v) => setField("degreeLevel", v as ThesisDegreeLevel)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(DEGREE_LEVEL_LABELS) as ThesisDegreeLevel[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {DEGREE_LEVEL_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <RequiredLabel required>Binding type</RequiredLabel>
            <Select
              value={form.bindingType}
              onValueChange={(v) => setField("bindingType", v as ThesisBindingType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(BINDING_TYPE_LABELS) as ThesisBindingType[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {BINDING_TYPE_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <RequiredLabel htmlFor="title" required error={errorFor("title")}>
            Title
          </RequiredLabel>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            onBlur={() => markTouched("title")}
            placeholder="Thesis or dissertation title"
            aria-invalid={Boolean(errorFor("title"))}
          />
        </div>

        <div className="space-y-1.5">
          <RequiredLabel htmlFor="supervisor" required error={errorFor("supervisor")}>
            Supervisor
          </RequiredLabel>
          <Input
            id="supervisor"
            value={form.supervisor}
            onChange={(e) => setField("supervisor", e.target.value)}
            onBlur={() => markTouched("supervisor")}
            placeholder="Dr. / Prof. full name"
            aria-invalid={Boolean(errorFor("supervisor"))}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <NumberField
            id="pages"
            label="Pages"
            required
            min={MIN_PAGES}
            value={form.pageCount}
            onChange={(v) => setField("pageCount", v)}
            onBlur={() => markTouched("pageCount")}
            error={errorFor("pageCount")}
          />
          <NumberField
            id="copies"
            label="Copies"
            required
            min={MIN_COPIES}
            value={form.copies}
            onChange={(v) => setField("copies", v)}
            onBlur={() => markTouched("copies")}
            error={errorFor("copies")}
          />
          <NumberField
            id="color"
            label="Color pages"
            min={0}
            value={form.colorPages}
            onChange={(v) => setField("colorPages", v)}
          />
          <div className="space-y-1.5">
            <RequiredLabel htmlFor="paper">Paper</RequiredLabel>
            <Input
              id="paper"
              value={form.paperGrade}
              onChange={(e) => setField("paperGrade", e.target.value)}
              placeholder="80gsm"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <RequiredLabel
            htmlFor="fileUrl"
            required
            hint="Drive / Dropbox / OneDrive link"
            error={errorFor("fileUrl")}
          >
            Manuscript file URL
          </RequiredLabel>
          <Input
            id="fileUrl"
            value={form.fileUrl}
            onChange={(e) => setField("fileUrl", e.target.value)}
            onBlur={() => markTouched("fileUrl")}
            placeholder="https://drive.google.com/..."
            aria-invalid={Boolean(errorFor("fileUrl"))}
          />
          <p className="text-xs text-slate-500">
            Make sure the link is shareable (anyone with the link can view).
          </p>
        </div>

        <div className="space-y-1.5">
          <RequiredLabel htmlFor="notes">Formatting notes</RequiredLabel>
          <Textarea
            id="notes"
            rows={3}
            value={form.formattingNotes}
            onChange={(e) => setField("formattingNotes", e.target.value)}
            placeholder="Special instructions for the print shop"
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  min: number;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string;
}

function NumberField({
  id,
  label,
  min,
  value,
  onChange,
  onBlur,
  required,
  error,
}: NumberFieldProps) {
  return (
    <div className="space-y-1.5">
      <RequiredLabel htmlFor={id} required={required} error={error}>
        {label}
      </RequiredLabel>
      <Input
        id={id}
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
      />
    </div>
  );
}
